import { Component, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { plusIcon } from '@progress/kendo-svg-icons';

// Models
import { Customer } from '../../../../core/models/customer';

// Services
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { AccountTypeResult, Organization } from 'src/app/core/models/entities';
import { GeneralOrganizationService } from 'src/app/features/admin/organizations/components/general/service/general-organization.service';
import { Events, EventService } from 'src/app/core/services/event.service';
import { environment } from 'src/environments/environment';
import DataTable from 'datatables.net-dt';

export interface CustomerFilters {
  searchTerm: string;
  status: string;
  serviceType: string;
  dateRange: { start: Date; end: Date };
}

@Component({
  selector: 'app-customers',
  standalone: true,
  imports: [CommonModule, ButtonsModule, DropDownsModule, SVGIconModule, DatatableComponent],
  templateUrl: './customers.page.html',
  styleUrls: ['./customers.page.scss'],
})
export class CustomersPage extends BaseComponent {
  @ViewChild(DatatableComponent) private table?: DatatableComponent;

  private eventService = inject(EventService);
  private generalOrgService = inject(GeneralOrganizationService);

  // Data signals
  public customers = signal<Customer[]>([]);
  public isLoading = signal(false);
  public error = signal<string | null>(null);
  accountTypeItems = signal<AccountTypeResult[]>([]);

  private router = inject(Router);

  // Kendo SVG Icons
  public plusIcon = plusIcon;

  public statuses = [
    { text: 'Active', value: 1 },
    { text: 'Disabled', value: 0 },
  ];

  public datatableUrl = `${environment.apiUrl}/v1/organization/administrative/organization/`;

  private statusFilter: number | null = null;
  private accountTypeFilter: number | null = null;

  public initialFilters = this.buildFilters();

  public datatableColumns = [
    { data: 'name', name: 'name', title: 'Name', type: 'string', render: DataTable.render.text() },
    {
      data: 'status',
      name: 'status',
      title: 'Status',
      type: 'string',
      render: (data: number) =>
        data ? '<span class="badge bg-success">Active</span>' : '<span class="badge bg-danger">Inactive</span>',
    },
    {
      data: 'account_type_display',
      name: 'account_type',
      title: 'Type',
      type: 'string',
      render: DataTable.render.text(),
    },
  ];

  // Computed values
  public activeCustomers = computed(() => this.customers().filter((customer) => customer.status === 'active').length);
  public newCustomers = computed(() => this.customers().filter((customer) => customer.status === 'pending').length);
  public offBoardedCustomers = computed(
    () => this.customers().filter((customer) => customer.status === 'inactive').length
  );

  ngOnInit(): void {
    this.loadAccountTypes();
  }

  private loadAccountTypes(): void {
    this.generalOrgService
      .getAccountTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (accountTypes) => {
          this.accountTypeItems.set(accountTypes.results as AccountTypeResult[]);
        },
        error: (error) => {
          console.error('Error fetching account types:', error);
        },
      });
  }

  private buildFilters(): Record<string, string> {
    const filters: Record<string, string> = { $orderby: 'name' };
    const clauses: string[] = [];
    if (this.statusFilter != null) {
      clauses.push(`status eq ${this.statusFilter}`);
    }
    if (this.accountTypeFilter != null) {
      clauses.push(`account_type eq ${this.accountTypeFilter}`);
    }
    if (clauses.length) {
      filters['$filter'] = clauses.join(' and ');
    }
    return filters;
  }

  public statusChange($event: { text: string; value: number | null }): void {
    this.statusFilter = $event?.value ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public accountTypeChange($event: { display_text: string; id: number | null }): void {
    this.accountTypeFilter = $event?.id ?? null;
    this.table?.setFilters(this.buildFilters());
  }

  public onAddCustomer(): void {
    this.router.navigate(['/customer/msp-portal/customers/customer-onboarding']);
  }

  public onCustomerSelect(customer: Customer): void {
    // Handle customer selection - navigate to detail view
    console.log('Selected customer:', customer);
  }

  public onCustomerEdit(customer: Customer): void {
    // Handle customer edit
    console.log('Edit customer:', customer);
  }

  public onCustomerDelete(customer: Customer): void {
    // Handle customer deletion
    console.log('Delete customer:', customer);
  }

  viewOrg(row: unknown) {
    const organization = row as Organization;
    this.router.navigate(['/customer/msp-portal/customers/customer-details'], {
      queryParams: {
        organizationId: organization.id,
        account_type: organization.account_type,
      },
    });
    this.eventService.emit(Events.OrgSelectionChange.type, new Events.WhiteLabelChange(organization));
  }
}
